import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import {
  BarChart2, Plus, TrendingUp, TrendingDown, Minus, Hash, ToggleLeft, AlignLeft, Gauge, Clock,
  Building2, Trophy, Users,
} from 'lucide-react'
import { ProtectedRoute } from '../../components/ProtectedRoute'
import { useAuth } from '../../hooks/useAuth'
import { can } from '../../lib/roles'
import { DEFAULT_COMPANY_ID } from '../../lib/config'
import {
  listMetricDefinitions,
  createMetricDefinition,
  listMetricSnapshots,
  createMetricSnapshot,
  getMetricAggregate,
  type MetricDefinitionDto,
  type MetricSnapshotDto,
  type MetricAggregateDto,
} from '../../lib/metrics'
import { listUsers, type UserDto } from '../../lib/users'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { MetricTrendChart } from '@/components/charts/MetricTrendChart'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

export const Route = createFileRoute('/kinerja/metrics')({
  component: MetricsPage,
})

function MetricsPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <MetricsContent />
    </ProtectedRoute>
  )
}

const METRIC_TYPE_META: Record<string, { label: string; icon: React.ElementType; variant: 'default' | 'secondary' | 'navy' | 'success' | 'muted' }> = {
  likert_5:  { label: 'Likert 1–5', icon: Gauge,      variant: 'default'    },
  likert_7:  { label: 'Likert 1–7', icon: Gauge,      variant: 'secondary'  },
  numeric:   { label: 'Numeric',    icon: Hash,        variant: 'navy'       },
  boolean:   { label: 'Boolean',    icon: ToggleLeft,  variant: 'success'    },
  open_text: { label: 'Open Text',  icon: AlignLeft,   variant: 'muted'      },
}

// Roles that can see the Company Overview toggle
const OVERVIEW_ROLES = new Set(['admin', 'hr', 'manager'])

// ─── Company Overview Panel ───────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up')   return <TrendingUp   className="w-4 h-4 text-green-500" />
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />
  return <Minus className="w-4 h-4 text-gray-400" />
}

function CompanyOverviewPanel({
  aggregates,
  loading,
}: {
  aggregates: MetricAggregateDto[]
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (aggregates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <BarChart2 className="w-10 h-10 text-muted-foreground/20" />
        <p className="text-sm text-muted-foreground">No metrics data available yet.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {aggregates.map((agg) => (
        <Card key={agg.metricDefinitionId} className="p-4 flex flex-col gap-3">
          {/* Metric header */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">{agg.name}</p>
              <p className="text-xs font-mono text-muted-foreground mt-0.5">{agg.code}</p>
            </div>
            <TrendIcon trend={agg.trend} />
          </div>

          {/* Company avg */}
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-bold tabular-nums text-foreground">
              {agg.companyAvg.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground">company avg</span>
          </div>

          {/* Division breakdown */}
          {agg.byDivision.length > 0 && (
            <div>
              <div className="flex items-center gap-1 mb-1.5">
                <Building2 className="w-3 h-3 text-muted-foreground" />
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">By Division</p>
              </div>
              <div className="space-y-1.5">
                {(() => {
                  const sorted = [...agg.byDivision].sort((a, b) => b.avg - a.avg).slice(0, 4)
                  const maxDivAvg = Math.max(...agg.byDivision.map((d) => d.avg), 0.01)
                  return sorted.map((div) => {
                    const pct = (div.avg / maxDivAvg) * 100
                    return (
                      <div key={div.divisionId} className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground truncate w-20 shrink-0">{div.name}</span>
                        <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-primary/60 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-mono text-foreground w-8 text-right shrink-0">
                          {div.avg.toFixed(1)}
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          <Users className="w-2.5 h-2.5 inline mr-0.5" />{div.employeeCount}
                        </span>
                      </div>
                    )
                  })
                })()}
              </div>
            </div>
          )}

          {/* Top performers */}
          {agg.topEmployees.length > 0 && (
            <div>
              <div className="flex items-center gap-1 mb-1.5">
                <Trophy className="w-3 h-3 text-amber-400" />
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Top Performers</p>
              </div>
              <div className="space-y-1">
                {agg.topEmployees.map((emp, idx) => (
                  <div key={emp.userId} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-muted-foreground w-4 shrink-0">{idx + 1}.</span>
                    <span className="text-[11px] text-foreground truncate flex-1">{emp.name}</span>
                    <span className="text-[11px] font-semibold tabular-nums text-primary">{emp.value.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  )
}

// ─── Main content ─────────────────────────────────────────────────────────────

function MetricsContent() {
  const { user } = useAuth()
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID
  const userId = user?.id
  const canManage = can(user?.role, 'manage:kinerja')
  const canSeeOverview = OVERVIEW_ROLES.has(user?.role ?? '')
  const isManager = user?.role === 'manager'

  // View mode — admins/HR/managers default to company overview
  const [viewMode, setViewMode] = useState<'company' | 'individual'>(
    canSeeOverview ? 'company' : 'individual'
  )
  // Division filter: managers default to their own division
  const [showMyDivision, setShowMyDivision] = useState(isManager)

  // Employees filtered by division for manager role
  const [allEmployees, setAllEmployees] = useState<UserDto[]>([])
  const filteredEmployees = useMemo(() => {
    if (!showMyDivision || !user?.divisionId) return allEmployees
    return allEmployees.filter((e) => e.divisionId === user.divisionId)
  }, [allEmployees, showMyDivision, user?.divisionId])

  const [definitions, setDefinitions] = useState<MetricDefinitionDto[]>([])
  const [defsLoading, setDefsLoading] = useState(true)
  const [selectedDef, setSelectedDef] = useState<MetricDefinitionDto | null>(null)
  const [snapshots, setSnapshots] = useState<MetricSnapshotDto[]>([])
  const [snapsLoading, setSnapsLoading] = useState(false)

  // Company aggregate state
  const [aggregates, setAggregates] = useState<MetricAggregateDto[]>([])
  const [aggLoading, setAggLoading] = useState(false)

  const [showCreateDef, setShowCreateDef] = useState(false)
  const [newDef, setNewDef] = useState({ code: '', name: '', type: 'numeric' as MetricDefinitionDto['type'] })
  const [defCreating, setDefCreating] = useState(false)
  const [defError, setDefError] = useState('')

  const [showAddSnap, setShowAddSnap] = useState(false)
  const [snap, setSnap] = useState({ subjectUserId: '__self__', value: '' })
  const [snapCreating, setSnapCreating] = useState(false)

  // Manager: employee selector for viewing snapshots
  const [viewUserId, setViewUserId] = useState<string>('')

  // Effect 1: load metric definitions
  useEffect(() => {
    let cancelled = false
    listMetricDefinitions(companyId).then((res) => {
      if (cancelled) return
      setDefsLoading(false)
      if (res.success && res.data) setDefinitions(res.data.definitions)
    })
    return () => { cancelled = true }
  }, [companyId])

  // Effect 2: load aggregate when switching to company view
  useEffect(() => {
    if (viewMode !== 'company' || !canSeeOverview) return
    let cancelled = false
    setAggLoading(true)
    getMetricAggregate(companyId).then((res) => {
      if (cancelled) return
      setAggLoading(false)
      if (res.success && res.data) setAggregates(res.data.aggregates)
    })
    return () => { cancelled = true }
  }, [viewMode, canSeeOverview, companyId])

  // Effect 3: load snapshots when a definition is selected (or viewUserId changes)
  useEffect(() => {
    if (!selectedDef || !userId) return
    const targetId = viewUserId || userId
    let cancelled = false
    setSnapsLoading(true)
    listMetricSnapshots(companyId, targetId).then((res) => {
      if (cancelled) return
      setSnapsLoading(false)
      if (res.success && res.data) {
        setSnapshots(res.data.snapshots.filter((s) => s.metricDefinitionId === selectedDef.id))
      }
    })
    return () => { cancelled = true }
  }, [selectedDef, companyId, userId, viewUserId])

  // Effect 4: load employees when manage dialog opens or canManage becomes true
  useEffect(() => {
    if (!canManage) return
    let cancelled = false
    listUsers(companyId, { isActive: true }).then((res) => {
      if (cancelled) return
      if (res.success && res.data) setAllEmployees(res.data.users)
    })
    return () => { cancelled = true }
  }, [canManage, companyId])

  async function handleCreateDef(e: React.FormEvent) {
    e.preventDefault()
    setDefCreating(true)
    setDefError('')
    const res = await createMetricDefinition({ companyId, ...newDef })
    setDefCreating(false)
    if (res.success && res.data) {
      toast.success('Metric definition created')
      setDefinitions((prev) => [...prev, res.data!.definition])
      setShowCreateDef(false)
      setNewDef({ code: '', name: '', type: 'numeric' })
    } else {
      toast.error(res.error ?? 'Failed to create metric')
      setDefError(res.error ?? 'Failed')
    }
  }

  async function handleAddSnapshot(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedDef || !userId) return
    setSnapCreating(true)
    const resolvedUserId = (snap.subjectUserId === '__self__' || snap.subjectUserId === '')
      ? userId
      : snap.subjectUserId
    // Coerce value to correct type based on metric type
    let coercedValue: number | boolean | string = snap.value;
    if (selectedDef.type === 'boolean') {
      coercedValue = snap.value === 'true' || snap.value === '1';
    } else if (selectedDef.type !== 'open_text') {
      const parsed = Number(snap.value);
      coercedValue = isNaN(parsed) ? snap.value : parsed;
    }

    const res = await createMetricSnapshot({
      companyId,
      metricDefinitionId: selectedDef.id,
      subjectUserId: resolvedUserId,
      value: coercedValue,
      source: 'manual',
    })
    setSnapCreating(false)
    if (res.success && res.data) {
      toast.success('Measurement recorded')
      setSnapshots((prev) => [res.data!.snapshot, ...prev])
      setShowAddSnap(false)
      setSnap({ subjectUserId: '__self__', value: '' })
    } else {
      toast.error('Failed to record measurement')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Metrics</h1>
          <p className="text-sm text-muted-foreground mt-1">KPI definitions and measurement history</p>
        </div>
        <div className="flex items-center gap-2">
          {canSeeOverview && (
            <div className="flex items-center rounded-lg border bg-muted p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setViewMode('company')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  viewMode === 'company'
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Company Overview
              </button>
              <button
                type="button"
                onClick={() => setViewMode('individual')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  viewMode === 'individual'
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Individual
              </button>
            </div>
          )}
          {isManager && user?.divisionId && (
            <div className="flex items-center rounded-lg border bg-muted p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setShowMyDivision(true)}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  showMyDivision
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                My Division
              </button>
              <button
                type="button"
                onClick={() => setShowMyDivision(false)}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-all',
                  !showMyDivision
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                All
              </button>
            </div>
          )}
          {canManage && (
            <Button size="sm" onClick={() => setShowCreateDef(true)}>
              <Plus className="w-4 h-4" />
              New Metric
            </Button>
          )}
        </div>
      </div>

      {/* ── Company Overview Mode ─────────────────────────────────────────── */}
      {viewMode === 'company' && canSeeOverview && (
        <div className="space-y-4">
          <CompanyOverviewPanel aggregates={aggregates} loading={aggLoading} />
        </div>
      )}

      {/* ── Individual Mode ───────────────────────────────────────────────── */}
      {viewMode === 'individual' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[calc(100vh-260px)]">
          {/* Left: definition list */}
          <Card className="md:col-span-1 flex flex-col overflow-hidden">
            <CardHeader className="py-3 px-4 border-b shrink-0">
              <CardTitle className="text-sm">Definitions</CardTitle>
            </CardHeader>
            <div className="flex-1 overflow-y-auto">
              {defsLoading ? (
                <div className="p-3 space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 bg-muted rounded-md animate-pulse" />
                  ))}
                </div>
              ) : definitions.length === 0 ? (
                <div className="p-6 text-center">
                  <BarChart2 className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No metrics defined yet.</p>
                  {canManage && (
                    <button
                      onClick={() => setShowCreateDef(true)}
                      className="text-xs text-primary hover:underline mt-2"
                    >
                      Create the first metric
                    </button>
                  )}
                </div>
              ) : (
                <div className="py-1">
                  {definitions.map((d, idx) => {
                    const meta = METRIC_TYPE_META[d.type]
                    const Icon = meta.icon
                    const isSelected = selectedDef?.id === d.id
                    return (
                      <div key={d.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedDef(d)}
                          className={cn(
                            'w-full text-left px-4 py-3 transition-colors hover:bg-accent',
                            isSelected && 'bg-brand-50'
                          )}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className={cn('p-1.5 rounded mt-0.5 shrink-0', isSelected ? 'bg-brand-100' : 'bg-muted')}>
                              <Icon className={cn('w-3 h-3', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                            </div>
                            <div className="min-w-0">
                              <p className={cn('text-sm font-medium truncate', isSelected ? 'text-primary' : 'text-foreground')}>
                                {d.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">{d.code} · {meta.label}</p>
                            </div>
                          </div>
                        </button>
                        {idx < definitions.length - 1 && <Separator />}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Right: detail + history */}
          <div className="md:col-span-2">
            {selectedDef ? (
              <Card className="h-full flex flex-col overflow-hidden">
                <CardHeader className="py-3 px-5 border-b shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">{selectedDef.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground font-mono">{selectedDef.code}</span>
                        <Badge variant={METRIC_TYPE_META[selectedDef.type].variant} className="text-[10px]">
                          {METRIC_TYPE_META[selectedDef.type].label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canManage && allEmployees.length > 0 && (
                        <Select
                          value={viewUserId || '__self__'}
                          onValueChange={(v) => setViewUserId(v === '__self__' ? '' : v)}
                        >
                          <SelectTrigger className="h-8 text-xs w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__self__">— My data —</SelectItem>
                            {filteredEmployees.map((e) => (
                              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => setShowAddSnap(true)}>
                          <Plus className="w-3.5 h-3.5" />
                          Add
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <div className="flex-1 overflow-y-auto">
                  <div className="p-5 space-y-5">
                    {/* Trend chart */}
                    {snapshots.length >= 2 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Trend
                        </p>
                        <MetricTrendChart
                          data={[...snapshots]
                            .sort((a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime())
                            .filter((s) => typeof s.value === 'number')
                            .map((s) => ({
                              label: new Date(s.capturedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                              value: s.value as number,
                            }))}
                          height={160}
                        />
                      </div>
                    )}

                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                        History
                      </p>
                      {snapsLoading ? (
                        <div className="space-y-2">
                          {[1, 2].map((i) => (
                            <div key={i} className="h-12 bg-muted rounded-md animate-pulse" />
                          ))}
                        </div>
                      ) : snapshots.length === 0 ? (
                        <div className="py-10 text-center">
                          <TrendingUp className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No measurements recorded yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {snapshots.map((s) => (
                            <div key={s.id} className="flex items-center gap-3 bg-muted/50 rounded-md px-3 py-2.5">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(s.capturedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </p>
                                  <span className="text-xs text-muted-foreground/50">·</span>
                                  <span className="text-xs text-muted-foreground capitalize">{s.source}</span>
                                </div>
                              </div>
                              <span className="font-semibold text-foreground tabular-nums">{String(s.value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <div className="text-center">
                  <BarChart2 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Select a metric to view its history</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create definition dialog */}
      <Dialog open={showCreateDef} onOpenChange={setShowCreateDef}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Metric Definition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDef} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="def-code">Code</Label>
              <Input id="def-code" required placeholder="e.g. PRODUCTIVITY"
                value={newDef.code} onChange={(e) => setNewDef((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="def-name">Name</Label>
              <Input id="def-name" required placeholder="e.g. Productivity Score"
                value={newDef.name} onChange={(e) => setNewDef((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={newDef.type} onValueChange={(v) => setNewDef((f) => ({ ...f, type: v as MetricDefinitionDto['type'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METRIC_TYPE_META).map(([v, m]) => (
                    <SelectItem key={v} value={v}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {defError && <p className="text-xs text-destructive">{defError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDef(false)}>Cancel</Button>
              <Button type="submit" disabled={defCreating}>{defCreating ? 'Creating…' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add snapshot dialog */}
      <Dialog open={showAddSnap} onOpenChange={setShowAddSnap}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Measurement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSnapshot} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={snap.subjectUserId} onValueChange={(v) => setSnap((f) => ({ ...f, subjectUserId: v }))}>
                <SelectTrigger><SelectValue placeholder="— Me —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">— Me (self) —</SelectItem>
                  {filteredEmployees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="snap-value">
                Value {selectedDef && <span className="text-muted-foreground font-normal">({METRIC_TYPE_META[selectedDef.type].label})</span>}
              </Label>
              <Input id="snap-value" required placeholder="e.g. 85"
                value={snap.value} onChange={(e) => setSnap((f) => ({ ...f, value: e.target.value }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddSnap(false)}>Cancel</Button>
              <Button type="submit" disabled={snapCreating}>{snapCreating ? 'Saving…' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
